/**
 * React wrapper for Bryntum ProjectModel
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { RefObject } from 'react';
import { AssignmentModel, AssignmentModelConfig, AssignmentStore, AssignmentStoreConfig, DependencyModel, DependencyModelConfig, DependencyStore, DependencyStoreConfig, EventModel, EventModelConfig, EventStore, EventStoreConfig, Model, ModelConfig, ProjectModel, ResourceModel, ResourceModelConfig, ResourceStore, ResourceStoreConfig, ResourceTimeRangeModel, ResourceTimeRangeModelConfig, ResourceTimeRangeStore, ResourceTimeRangeStoreConfig, StateTrackingManager, StateTrackingManagerConfig, Store, StoreConfig, TimeRangeModel, TimeRangeModelConfig, TimeRangeStore, TimeSpan } from '@bryntum/scheduler';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumSchedulerProjectModelProps = {
    // Configs
    /**
     * A flag, indicating whether the dates and duration calculations should adjust the result to DST time shift.
     */
    adjustDurationToDST? : boolean
    /**
     * The constructor of the assignment model class, to be used in the project. Will be set as the
     * [modelClass](https://bryntum.com/products/scheduler/docs/api/Core/data/Store#config-modelClass) property of the [assignmentStore](#Scheduler/model/ProjectModel#property-assignmentStore)
     */
    assignmentModelClass? : typeof AssignmentModel
    /**
     * Data use to fill the [assignmentStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-assignmentStore). Should be an array of
     * [AssignmentModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/AssignmentModel) or its configuration objects.
     */
    assignments? : AssignmentModel[]|AssignmentModelConfig[]
    /**
     * The initial data, to fill the [assignmentStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-assignmentStore) with.
     * Should be an array of [AssignmentModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/AssignmentModel) or its configuration
     * objects.
     */
    assignmentsData? : AssignmentModel[]|AssignmentModelConfig[]
    /**
     * An [AssignmentStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/AssignmentStore) instance or a config object.
     */
    assignmentStore? : AssignmentStore|AssignmentStoreConfig
    /**
     * The constructor to create an assignment store instance with. Should be a class, subclassing the
     * [AssignmentStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/AssignmentStore)
     */
    assignmentStoreClass? : typeof AssignmentStore|object
    /**
     * Child nodes. To allow loading children on demand, specify `children : true` in your data. Omit the field for leaf
     * tasks.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-children)
     */
    children? : boolean|object[]|Model[]|ModelConfig[]
    /**
     * Data use to fill the [dependencyStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-dependencyStore). Should be an array of
     * [DependencyModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/DependencyModel) or its configuration objects.
     */
    dependencies? : DependencyModel[]|DependencyModelConfig[]
    /**
     * The initial data, to fill the [dependencyStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-dependencyStore) with.
     * Should be an array of [DependencyModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/DependencyModel) or its configuration
     * objects.
     */
    dependenciesData? : DependencyModel[]|DependencyModelConfig[]
    /**
     * The constructor of the dependency model class, to be used in the project. Will be set as the
     * [modelClass](https://bryntum.com/products/scheduler/docs/api/Core/data/Store#config-modelClass) property of the [dependencyStore](#Scheduler/model/ProjectModel#property-dependencyStore)
     */
    dependencyModelClass? : typeof DependencyModel
    /**
     * A [DependencyStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/DependencyStore) instance or a config object.
     */
    dependencyStore? : DependencyStore|DependencyStoreConfig
    /**
     * The constructor to create a dependency store instance with. Should be a class, subclassing the
     * [DependencyStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/DependencyStore)
     */
    dependencyStoreClass? : typeof DependencyStore|object
    /**
     * The constructor of the event model class, to be used in the project. Will be set as the
     * [modelClass](https://bryntum.com/products/scheduler/docs/api/Core/data/Store#config-modelClass) property of the [eventStore](#Scheduler/model/ProjectModel#property-eventStore)
     */
    eventModelClass? : typeof EventModel
    /**
     * Data use to fill the [eventStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-eventStore). Should be an array of
     * [EventModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/EventModel) or its configuration objects.
     */
    events? : EventModel[]|EventModelConfig[]
    /**
     * The initial data, to fill the [eventStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-eventStore) with.
     * Should be an array of [EventModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/EventModel) or its configuration objects.
     */
    eventsData? : EventModel[]|EventModelConfig[]
    /**
     * An [EventStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/EventStore) instance or a config object.
     */
    eventStore? : EventStore|EventStoreConfig
    /**
     * The constructor to create an event store instance with. Should be a class, subclassing the
     * [EventStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/EventStore)
     */
    eventStoreClass? : typeof EventStore|object
    /**
     * Start expanded or not (only valid for tree data)
     */
    expanded? : boolean
    /**
     * Unique identifier for the record. Might be mapped to another dataSource using idField, but always exposed as
     * record.id. Will get a generated value if none is specified in records data.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-id)
     */
    id? : string|number
    /**
     * Project data as a JSON string, used to populate its stores.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-json)
     */
    json? : string
    /**
     * This is a read-only field provided in server synchronization packets to specify
     * which position the node takes in the parent's ordered children array.
     * This index is set on load and gets updated on reordering nodes in tree. Sorting and filtering
     * have no effect on it.
     */
    orderedParentIndex? : number
    /**
     * This is a read-only field provided in server synchronization packets to specify
     * which record id is the parent of the record.
     */
    parentId? : string|number|null
    /**
     * This is a read-only field provided in server synchronization packets to specify
     * which position the node takes in the parent's children array.
     * This index is set on load and gets updated automatically after row reordering, sorting, etc.
     * To save the order, need to persist the field on the server and when data is fetched to be loaded,
     * need to sort by this field.
     */
    parentIndex? : number
    /**
     * Flag the record as read-only on the UI level, preventing the end user from manipulating it using editing
     * features such as cell editing and event dragging.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-readOnly)
     */
    readOnly? : boolean
    /**
     * This field is added to the class at runtime when the Store is configured with
     * [lazyLoad](https://bryntum.com/products/scheduler/docs/api/Core/data/Store#config-lazyLoad). It is also required for lazy load functionality in a Tree store. The
     * number specified should reflect the <strong>total</strong> amount of children of a parent node, including nested descendants.
     */
    remoteChildCount? : number
    /**
     * The constructor of the resource model class, to be used in the project. Will be set as the
     * [modelClass](https://bryntum.com/products/scheduler/docs/api/Core/data/Store#config-modelClass) property of the [resourceStore](#Scheduler/model/ProjectModel#property-resourceStore)
     */
    resourceModelClass? : typeof ResourceModel
    /**
     * Data use to fill the [resourceStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-resourceStore). Should be an array of
     * [ResourceModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ResourceModel) or its configuration objects.
     */
    resources? : ResourceModel[]|ResourceModelConfig[]
    /**
     * The initial data, to fill the [resourceStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-resourceStore) with.
     * Should be an array of [ResourceModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ResourceModel) or its configuration objects.
     */
    resourcesData? : ResourceModel[]|ResourceModelConfig[]
    /**
     * A [ResourceStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/ResourceStore) instance or a config object.
     */
    resourceStore? : ResourceStore|ResourceStoreConfig
    /**
     * The constructor to create a resource store instance with. Should be a class, subclassing the
     * [ResourceStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/ResourceStore)
     */
    resourceStoreClass? : typeof ResourceStore|object
    /**
     * Data use to fill the [resourceTimeRangeStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-resourceTimeRangeStore). Should be an array
     * of [ResourceTimeRangeModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ResourceTimeRangeModel) or its configuration objects.
     */
    resourceTimeRanges? : ResourceTimeRangeModel[]|ResourceTimeRangeModelConfig[]
    /**
     * The initial data, to fill the [resourceTimeRangeStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/mixin/ProjectModelMixin#property-resourceTimeRangeStore) with.
     * Should be an array of [ResourceTimeRangeModel](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ResourceTimeRangeModel) or it's
     * configuration objects.
     */
    resourceTimeRangesData? : ResourceTimeRangeModel[]
    /**
     * A [ResourceTimeRangeStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/ResourceTimeRangeStore) instance or a config object.
     */
    resourceTimeRangeStore? : ResourceTimeRangeStore|ResourceTimeRangeStoreConfig
    /**
     * The constructor to create a resource time range store instance with. Should be a class subclassing the
     * [ResourceTimeRangeStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/ResourceTimeRangeStore)
     */
    resourceTimeRangeStoreClass? : typeof ResourceTimeRangeStore|object
    /**
     * Experimental hook that lets the app determine if a bound dataset needs syncing with the store or not, and
     * if it does - which records that should be processed. Only called for stores that are configured with
     * `syncDataOnLoad: true` (which is the default in the React, Angular and Vue wrappers).
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-shouldSyncDataOnLoad)
     * @param {object} options Options passed by the store to this hook
     * @param {Store} options.store Store about to be synced
     * @param {Model} options.records Records currently in the store
     * @param {<Object>} options.data Incoming data
     * @returns {Set|boolean} Return `false` to prevent the store from syncing, or a set of record ids that need further processing (for records that has some kind of change, eg. an update, removal or addition)
     */
    shouldSyncDataOnLoad? : (options: { store: Store, records: Model, data: object[] }) => Set<any>|boolean
    /**
     * Silences propagations caused by the project loading.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-silenceInitialCommit)
     */
    silenceInitialCommit? : boolean
    /**
     * Configuration options to provide to the STM manager
     */
    stm? : StateTrackingManagerConfig|StateTrackingManager
    /**
     * Data use to fill the [timeRangeStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#property-timeRangeStore). Should be an array of
     * [TimeRangeModels](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/TimeRangeModel) or its configuration objects.
     */
    timeRanges? : TimeRangeModel[]|TimeRangeModelConfig[]
    /**
     * The initial data, to fill the [timeRangeStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/mixin/ProjectModelMixin#property-timeRangeStore) with.
     * Should be an array of [TimeSpan](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/TimeSpan) or its configuration objects.
     */
    timeRangesData? : TimeSpan[]
    /**
     * A [Store](https://bryntum.com/products/scheduler/docs/api/Core/data/Store) instance or a config object.
     */
    timeRangeStore? : Store|StoreConfig
    /**
     * The constructor to create a time range store instance with. Should be a class subclassing the
     * [TimeRangeStore](https://bryntum.com/products/scheduler/docs/api/Scheduler/data/TimeRangeStore)
     */
    timeRangeStoreClass? : typeof TimeRangeStore|object
    /**
     * Set to a IANA time zone (i.e. `Europe/Stockholm`) or a UTC offset in minutes (i.e. `-120`). This will
     * convert all events, tasks and time ranges to the specified time zone or offset. It will also affect the
     * displayed timeline's headers as well at the start and end date of it.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-timeZone)
     */
    timeZone? : string|number
    /**
     * Specifies the output format of [toJSON](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/mixin/ProjectModelCommon#function-toJSON).
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-toJSONResultFormat)
     */
    toJSONResultFormat? : 'inlineData'|'model'
    /**
     * By default, the stores of a project use the raw data objects passed to them as the data source for their
     * records if data is loaded remotely (using an `AjaxStore` or a `CrudManager`). For data supplied inline,
     * the data objects are instead by default cloned to avoid the original data object being modified by the
     * store.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#config-useRawData)
     */
    useRawData? : boolean

    // Events
    /**
     * Fired when data in any of the projects stores changes.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#event-change)
     * @param {object} event Event object
     * @param {ProjectModel|any} event.source This project
     * @param {Store} event.store Affected store
     * @param {'remove'|'removeAll'|'add'|'clearchanges'|'filter'|'update'|'dataset'|'replace'} event.action Name of action which triggered the change. May be one of:  * `'remove'` * `'removeAll'` * `'add'` * `'clearchanges'` * `'filter'` * `'update'` * `'dataset'` * `'replace'`
     * @param {Model} event.record Changed record, for actions that affects exactly one record (`'update'`)
     * @param {Model[]} event.records Changed records, passed for all actions except `'removeAll'`
     * @param {object} event.changes Passed for the `'update'` action, info on which record fields changed
     */
    onChange? : ((event: { source: ProjectModel|any, store: Store, action: 'remove'|'removeAll'|'add'|'clearchanges'|'filter'|'update'|'dataset'|'replace', record: Model, records: Model[], changes: object }) => void)|string
    /**
     * Fired when the engine has finished its calculations and the results has been written back to the records.
     * ...
     * [View online docs...](https://bryntum.com/products/scheduler/docs/api/Scheduler/model/ProjectModel#event-dataReady)
     * @param {object} event Event object
     * @param {ProjectModel|any} event.source The project
     * @param {boolean} event.isInitialCommit Flag that shows if this commit is initial
     * @param {Set<any>} event.records Set of all [Model](https://bryntum.com/products/scheduler/docs/api/Core/data/Model)s that were modified in the completed transaction. Use the [modifications](https://bryntum.com/products/scheduler/docs/api/Core/data/Model#property-modifications) property of each Model to identify modified fields.
     */
    onDataReady? : ((event: { source: ProjectModel|any, isInitialCommit: boolean, records: Set<any> }) => void)|string

}

export class BryntumSchedulerProjectModel extends React.Component<BryntumSchedulerProjectModelProps> {

    static instanceClass = ProjectModel;

    static instanceName = 'ProjectModel';

    processWidgetContent = processWidgetContent;

    dataStores = {
        'assignmentStore': 'assignments',
        'dependencyStore': 'dependencies',
        'eventStore': 'events',
        'resourceStore': 'resources',
        'resourceTimeRangeStore': 'resourceTimeRanges',
        'timeRangeStore': 'timeRanges'
    };

    static configNames = [
        'adjustDurationToDST',
        'assignmentModelClass',
        'assignmentsData',
        'assignmentStoreClass',
        'children',
        'dependenciesData',
        'dependencyModelClass',
        'dependencyStoreClass',
        'eventModelClass',
        'eventsData',
        'eventStoreClass',
        'expanded',
        'orderedParentIndex',
        'parentId',
        'parentIndex',
        'resourceModelClass',
        'resourcesData',
        'resourceStoreClass',
        'resourceTimeRangesData',
        'resourceTimeRangeStoreClass',
        'silenceInitialCommit',
        'timeRangesData',
        'timeRangeStoreClass',
        'toJSONResultFormat',
        'useRawData'
    ];

    static propertyConfigNames = [
        'assignments',
        'assignmentStore',
        'dependencies',
        'dependencyStore',
        'events',
        'eventStore',
        'id',
        'json',
        'onChange',
        'onDataReady',
        'readOnly',
        'remoteChildCount',
        'resources',
        'resourceStore',
        'resourceTimeRanges',
        'resourceTimeRangeStore',
        'shouldSyncDataOnLoad',
        'stm',
        'timeRanges',
        'timeRangeStore',
        'timeZone'
    ];

    static propertyNames = [
        'allChildren',
        'descendantCount',
        'hasGeneratedId',
        'inlineData',
        'internalId',
        'isCommitting',
        'isCreating',
        'isValid',
        'previousSiblingsTotalCount',
        'visibleDescendantCount'
    ];

    // Component instance
    instance!: ProjectModel;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumSchedulerProjectModelProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        return null

    }
}
